/**
 * FGX-609 — Shared Pairs Well With recommendations resolver.
 * Resolves complementary product sources, fetches API markup, and adapts it to the host context.
 */
class PairsWellWithRecommendations extends HTMLElement {
  static VALID_CONTEXTS = ['cart', 'minicart'];

  constructor() {
    super();
    this.apiSectionId = 'pairs-well-with-api';
    this.metafieldSectionId = 'pairs-well-with-metafield';
    this.fetchLimit = parseInt(this.dataset.fetchLimit, 10) || 10;
    this.displayLimit = parseInt(this.dataset.displayLimit, 10) || 4;
    this.hasCustomHeading = this.hasAttribute('data-heading');
    this.heading = this.dataset.heading;
    this.paddingTop = this.dataset.paddingTop || '24';
    this.paddingBottom = this.dataset.paddingBottom || '24';
    this.paddingHorizontal = this.dataset.paddingHorizontal || '0';
    this.pdpSourceProductId = this.dataset.pdpSourceId || null;
    this.resolvedSourceProductId = null;
    /** @type {Set<string>} */
    this.cartProductIds = new Set();
    /** @type {Map<string, string>} */
    this.cartProductIdToUrl = new Map();
    /** @type {Set<string>} Minicart session: products added via PWW during the current session. */
    this.minicartSessionPwwAddedProductIds = new Set();
    this.isLoading = false;
    this.cartUpdateUnsubscriber = undefined;
    this.context = null;
  }

  connectedCallback() {
    this.context = this.dataset.context;

    if (!PairsWellWithRecommendations.VALID_CONTEXTS.includes(this.context)) {
      console.error(
        `[Pairs Well With] Invalid or missing data-context "${this.context}". Expected "cart" or "minicart".`
      );
      this.hideComponent();
      return;
    }

    this.applyHostVariables();
    this.initialize();

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source === 'pairs-well-with-resolver') return;
      if (this.context === 'minicart' && event?.source === 'product-form') return;
      this.handleCartUpdate(event);
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  /**
   * Applies padding CSS variables from host data attributes.
   */
  applyHostVariables() {
    this.style.setProperty('--pairs-well-with-padding-top', `${this.paddingTop}px`);
    this.style.setProperty('--pairs-well-with-padding-bottom', `${this.paddingBottom}px`);
    this.style.setProperty('--pairs-well-with-padding-horizontal', `${this.paddingHorizontal}px`);
  }

  /**
   * Boots source resolution for the configured host context.
   */
  async initialize() {
    if (this.isLoading) return;
    this.isLoading = true;

    try {
      const cart = await this.fetchCart();
      const orderedProductIds = this.getOrderedUniqueProductIds(cart.items);

      if (this.context === 'minicart') {
        const resolved = await this.tryPdpSource(orderedProductIds);

        if (!resolved) {
          this.resolvedSourceProductId = null;
          this.hideComponent();
        }

        return;
      }

      await this.resolveSourceFromCart(orderedProductIds);
    } catch (error) {
      console.error('[Pairs Well With] Initialization failed:', error);
      this.hideComponent();
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Starts a Minicart session. Driven by rea-minicart.js, which owns the session lifecycle,
   * rather than inferred from add events, so it runs before any add response can race it.
   * Every session starts from the full recommendation set for the PDP source this component
   * already holds: the pre-session cart never filters it, and a later add never moves it.
   */
  startMinicartSession() {
    if (this.context !== 'minicart') return;

    this.minicartSessionPwwAddedProductIds.clear();
    this.resolvedSourceProductId = null;
    // Slides are removed destructively while a session is active. Clear that rendered
    // subset now; the next bundled/async PWW render repopulates the PDP-source set.
    this.hideComponent();
  }

  /**
   * Excludes a product added from PWW for the rest of the current session.
   * @param {number|string} productId
   */
  addMinicartSessionPwwProduct(productId) {
    if (this.context !== 'minicart' || !productId) return;
    this.minicartSessionPwwAddedProductIds.add(String(productId));
  }

  /**
   * Removes already-excluded products from the carousel currently on screen. Used when a
   * successful PWW add arrives after a newer one and its bundled markup cannot be applied.
   */
  applyMinicartSessionExclusions() {
    if (this.context !== 'minicart') return;

    let removed = false;
    this.querySelectorAll('.pairs-well-with__slide').forEach((slide) => {
      if (!this.minicartSessionPwwAddedProductIds.has(String(slide.dataset.productId))) return;
      slide.remove();
      removed = true;
    });

    if (!removed) return;

    if (!this.querySelector('.pairs-well-with__slide')) {
      this.hideComponent();
      return;
    }

    this.querySelector('.swiper')?.swiper?.update();
  }

  /** Ends the Minicart session. No exclusion may outlive a closed Minicart. */
  endMinicartSession() {
    if (this.context !== 'minicart') return;

    this.minicartSessionPwwAddedProductIds.clear();
    this.resolvedSourceProductId = null;
  }

  /**
   * Synchronous Minicart PWW update from bundled section HTML in cart/add response.
   * @param {Object} event
   * @param {string} sectionHtml
   * @returns {boolean} true when bundled path fully handled (no async fallback)
   */
  handleBundledCartUpdate(event, sectionHtml) {
    if (this.context !== 'minicart' || !sectionHtml) return false;

    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const wrapper = doc.querySelector('.pairs-well-with-metafield-source');
    if (!wrapper) return false;

    const bundledSourceId = wrapper.dataset.sourceProductId;
    if (!bundledSourceId) return false;

    const expectedSource =
      event?.pwwContext === 'minicart'
        ? String(this.pdpSourceProductId || '')
        : String(event?.cartData?.product_id || '');

    if (!expectedSource || bundledSourceId !== expectedSource) {
      if (event?.pwwContext !== 'minicart') {
        this.hideComponent();
      }
      return false;
    }

    if (wrapper.getAttribute('data-has-complementary') !== 'true') {
      this.resolvedSourceProductId = null;
      this.hideComponent();
      return true;
    }

    if (!wrapper.querySelector('.pairs-well-with')) {
      return false;
    }

    const rendered = this.renderRecommendations({
      recommendations: wrapper,
    });

    if (rendered) {
      this.resolvedSourceProductId = Number(expectedSource);
    } else {
      this.resolvedSourceProductId = null;
    }

    return true;
  }

  /**
   * Handles cart updates while preserving a valid source when possible.
   * @param {Object} [event]
   */
  async handleCartUpdate(event) {
    try {
      const cart = await this.fetchCart();
      const orderedProductIds = this.getOrderedUniqueProductIds(cart.items);

      if (this.context === 'minicart') {
        const resolved = await this.tryPdpSource(orderedProductIds);

        if (!resolved) {
          this.resolvedSourceProductId = null;
          this.hideComponent();
        }

        return;
      }

      if (this.resolvedSourceProductId && orderedProductIds.includes(this.resolvedSourceProductId)) {
        const result = await this.fetchRecommendationsResponse(this.resolvedSourceProductId);
        if (result.failed) {
          this.hideComponent();
          return;
        }
        if (result.hasRawComplementary) {
          const rendered = this.renderRecommendations(result);
          if (rendered) return;
        }
      }

      await this.resolveSourceFromCart(orderedProductIds);
    } catch (error) {
      console.error('[Pairs Well With] Cart update handling failed:', error);
      this.hideComponent();
    }
  }

  /**
   * Preserves a PDP source for minicart hosts when it remains valid.
   * @param {number[]} orderedProductIds
   * @returns {Promise<boolean>}
   */
  async tryPdpSource(orderedProductIds) {
    const pdpId = Number(this.pdpSourceProductId);
    if (!pdpId || !orderedProductIds.includes(pdpId)) return false;

    const result = await this.fetchMetafieldRecommendationsResponse(pdpId);
    if (result.failed) {
      this.hideComponent();
      return true;
    }
    if (!result.hasRawComplementary) return false;

    this.resolvedSourceProductId = pdpId;
    const rendered = this.renderRecommendations(result);
    if (!rendered) {
      this.resolvedSourceProductId = null;
      return false;
    }
    return true;
  }

  /**
   * Finds the first cart product with complementary recommendations and loads them.
   * @param {number[]} orderedProductIds
   */
  async resolveSourceFromCart(orderedProductIds) {
    for (const productId of orderedProductIds) {
      const result = await this.fetchRecommendationsResponse(productId);
      if (result.failed) continue;
      if (result.hasRawComplementary) {
        const rendered = this.renderRecommendations(result);
        if (rendered) {
          this.resolvedSourceProductId = productId;
          return;
        }
      }
    }

    this.resolvedSourceProductId = null;
    this.hideComponent();
  }

  /**
   * Returns unique product IDs preserving cart item order (newest first).
   * @param {Object[]} items
   * @returns {number[]}
   */
  getOrderedUniqueProductIds(items = []) {
    const seen = new Set();
    const ordered = [];

    items.forEach((item) => {
      const productId = Number(item.product_id);
      if (!productId || seen.has(productId)) return;
      seen.add(productId);
      ordered.push(productId);
    });

    return ordered;
  }

  /**
   * Fetches current cart state.
   * @returns {Promise<Object>}
   */
  async fetchCart() {
    const response = await fetch(`${routes.cart_url}.js`);
    const cart = await response.json();
    this.refreshCartProductIds(cart);
    this.refreshCartProductUrls(cart);
    return cart;
  }

  /**
   * Refreshes product_id → product URL map from cart line items (string keys).
   * @param {Object} cart
   */
  refreshCartProductUrls(cart) {
    this.cartProductIdToUrl = new Map();

    (cart?.items || []).forEach((item) => {
      if (item.product_id == null || !item.url) return;
      this.cartProductIdToUrl.set(String(item.product_id), item.url);
    });
  }

  /**
   * Refreshes the in-memory set of cart product IDs (string-normalized).
   * @param {Object} cart
   */
  refreshCartProductIds(cart) {
    this.cartProductIds = new Set(
      (cart?.items || [])
        .map((item) => (item.product_id != null ? String(item.product_id) : ''))
        .filter(Boolean)
    );
  }

  /**
   * Filters API slides for the host context and caps to the display limit.
   * @param {Element} root `.pairs-well-with` aside
   * @returns {number} remaining slide count
   */
  prepareEligibleSlides(root) {
    const slides = [...root.querySelectorAll('.pairs-well-with__slide')];
    const exclusionSet =
      this.context === 'cart' ? this.cartProductIds : this.minicartSessionPwwAddedProductIds;

    slides.forEach((slide) => {
      const productId = slide.dataset.productId;
      if (!productId || exclusionSet.has(String(productId))) {
        slide.remove();
      }
    });

    const remaining = [...root.querySelectorAll('.pairs-well-with__slide')];
    remaining.slice(this.displayLimit).forEach((slide) => slide.remove());

    return root.querySelectorAll('.pairs-well-with__slide').length;
  }

  /**
   * Builds a Section Rendering URL on the source product, preserving existing query params.
   * @param {string} productUrl
   * @param {string} sectionId
   * @returns {string}
   */
  buildProductSectionRenderUrl(productUrl, sectionId) {
    const resolved = new URL(productUrl, window.location.href);
    resolved.searchParams.set('section_id', sectionId);
    return resolved.toString();
  }

  /**
   * Fetches complementary candidates from Search & Discovery metafield (Minicart only).
   * @param {number|string} productId
   * @returns {Promise<{recommendations: Element|null, hasRawComplementary: boolean, failed: boolean}>}
   */
  async fetchMetafieldRecommendationsResponse(productId) {
    const productKey = String(productId);
    const productUrl = this.cartProductIdToUrl.get(productKey);

    if (!productUrl) {
      console.error('[Pairs Well With] No cart product URL for metafield section render:', productKey);
      return { recommendations: null, hasRawComplementary: false, failed: true };
    }

    const url = this.buildProductSectionRenderUrl(productUrl, this.metafieldSectionId);

    try {
      const response = await fetch(url);

      if (!response.ok) {
        console.error(`[Pairs Well With] Metafield section render failed (${response.status}): ${url}`);
        return { recommendations: null, hasRawComplementary: false, failed: true };
      }

      const text = await response.text();
      const html = document.createElement('div');
      html.innerHTML = text;
      const recommendations = html.querySelector('.pairs-well-with-metafield-source');

      if (!recommendations) {
        console.error(`[Pairs Well With] Metafield section wrapper not rendered: ${url}`);
        return { recommendations: null, hasRawComplementary: false, failed: true };
      }

      const hasRawComplementary = recommendations.getAttribute('data-has-complementary') === 'true';

      return { recommendations, hasRawComplementary, failed: false };
    } catch (error) {
      console.error('[Pairs Well With] Metafield section render request failed:', error);
      return { recommendations: null, hasRawComplementary: false, failed: true };
    }
  }

  /**
   * Fetches complementary recommendations for a product via Product Recommendations API (Cart only).
   * @param {number|string} productId
   * @returns {Promise<{recommendations: Element|null, hasRawComplementary: boolean, failed: boolean}>}
   */
  async fetchRecommendationsResponse(productId) {
    const sectionId = this.getApiSectionId();
    const shopifyRoot = window.Shopify?.routes?.root;

    if (!shopifyRoot) {
      console.error('[Pairs Well With] Shopify.routes.root is unavailable');
      return { recommendations: null, hasRawComplementary: false, failed: true };
    }

    const url = `${shopifyRoot}recommendations/products?section_id=${sectionId}&product_id=${productId}&limit=${this.fetchLimit}&intent=complementary`;

    try {
      const response = await fetch(url);

      if (!response.ok) {
        console.error(`[Pairs Well With] Recommendations API failed (${response.status}): ${url}`);
        return { recommendations: null, hasRawComplementary: false, failed: true };
      }

      const text = await response.text();
      const html = document.createElement('div');
      html.innerHTML = text;
      const recommendations = html.querySelector('product-recommendations');

      if (!recommendations) {
        console.error(`[Pairs Well With] Recommendations section not rendered: ${url}`);
        return { recommendations: null, hasRawComplementary: false, failed: true };
      }

      const hasRawComplementary = recommendations.getAttribute('data-has-complementary') === 'true';

      return { recommendations, hasRawComplementary, failed: false };
    } catch (error) {
      console.error('[Pairs Well With] Recommendations API request failed:', error);
      return { recommendations: null, hasRawComplementary: false, failed: true };
    }
  }

  /**
   * Renders recommendations from a fetched response or hides when none are eligible.
   * @param {{recommendations: Element|null}} result
   * @returns {boolean} whether eligible recommendations were rendered
   */
  renderRecommendations(result) {
    const { recommendations } = result;

    if (!recommendations || !recommendations.innerHTML.trim().length) {
      this.hideComponent();
      return false;
    }

    const aside = recommendations.querySelector('.pairs-well-with');
    if (!aside) {
      this.hideComponent();
      return false;
    }

    const eligibleCount = this.prepareEligibleSlides(aside);
    if (!eligibleCount) {
      this.hideComponent();
      return false;
    }

    this.adaptDetachedMarkup(aside);
    this.innerHTML = recommendations.innerHTML;
    this.removeAttribute('hidden');
    this.classList.remove('tw-hidden');
    this.applyHostVariables();

    if (this.hasCustomHeading) {
      const heading = this.querySelector('.pairs-well-with__heading');
      if (heading) heading.textContent = this.heading;
    }

    return true;
  }

  /**
   * Adapts neutral API markup to the host cart or minicart context before injection.
   * @param {Element} root
   */
  adaptDetachedMarkup(root) {
    const neutralClass = 'pairs-well-with--api';
    const contextClass = `pairs-well-with--${this.context}`;

    root.classList.remove(neutralClass);
    root.classList.add(contextClass);

    root.querySelectorAll('product-form').forEach((form) => {
      form.dataset.pwwContext = this.context;
    });

    const slideCount = root.querySelectorAll('.pairs-well-with__slide').length;
    const fewSlidesThreshold = this.context === 'cart' ? 4 : 3;
    root.classList.toggle('pairs-well-with--few-slides', slideCount < fewSlidesThreshold);

    this.configureSwiper(root);
  }

  /**
   * Returns the section id used for Recommendations API requests.
   * @returns {string}
   */
  getApiSectionId() {
    return this.apiSectionId;
  }

  /**
   * Applies context-specific Swiper options to injected markup.
   * @param {Element} root
   */
  configureSwiper(root) {
    const swiperSlider = root.querySelector('swiper-slider');
    if (!swiperSlider) return;

    const swiperConfig = swiperSlider.getAttribute('data-swiper');
    if (!swiperConfig) return;

    const options = JSON.parse(swiperConfig);
    const contextSelector = `.pairs-well-with--${this.context}`;

    options.loop = false;
    options.slidesPerGroup = 1;
    options.watchOverflow = true;
    options.navigation = {
      nextEl: `${contextSelector} .swiper-button-next`,
      prevEl: `${contextSelector} .swiper-button-prev`,
    };
    options.pagination = {
      el: `${contextSelector} .swiper-pagination`,
      type: 'bullets',
      clickable: true,
    };

    if (this.context === 'cart') {
      options.slidesPerView = 2;
      options.spaceBetween = 16;
      options.allowTouchMove = true;
      options.breakpoints = {
        768: {
          slidesPerView: 4,
          slidesPerGroup: 1,
          spaceBetween: 24,
          allowTouchMove: false,
        },
        1024: {
          slidesPerView: 'auto',
          slidesPerGroup: 1,
          spaceBetween: 24,
          allowTouchMove: false,
        },
      };
    } else {
      options.slidesPerView = 2;
      options.spaceBetween = 16;
      options.allowTouchMove = true;
      options.breakpoints = {
        768: {
          slidesPerView: 3,
          slidesPerGroup: 1,
          spaceBetween: 16,
          allowTouchMove: false,
        },
      };
    }

    swiperSlider.setAttribute('data-swiper', JSON.stringify(options));
  }

  /**
   * Hides the component when no eligible recommendations remain.
   */
  hideComponent() {
    this.innerHTML = '';
    this.setAttribute('hidden', '');
    this.classList.add('tw-hidden');
  }
}

if (!customElements.get('pairs-well-with-recommendations')) {
  customElements.define('pairs-well-with-recommendations', PairsWellWithRecommendations);
}
